import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { goalFor, PIECE_TYPE_ICON, PIECE_TYPE_LABEL } from "@/lib/campaigns";
import { ArrowLeft, Loader2, Trash2, Pencil, ExternalLink, Sparkles, CalendarDays } from "lucide-react";

const GEN_MESSAGES = [
  "Reading your Dive Brain…",
  "Locking the narrative arc…",
  "Drafting reels…",
  "Threading carousels…",
  "Bank of hooks incoming…",
  "Weaving pieces into one story…",
  "Almost surfacing…",
];

export default function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState({ campaign: null, pieces: [] });
  const [loading, setLoading] = useState(true);
  const [genMsgIdx, setGenMsgIdx] = useState(0);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get(`/campaigns/${id}`);
      setData(res);
      setLoading(false);
      return res.campaign;
    } catch (err) {
      console.debug("Campaign fetch failed", err);
      setLoading(false);
      return null;
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!data.campaign) return;
    if (data.campaign.status === "generating") {
      pollRef.current = setInterval(load, 4000);
      const msgTicker = setInterval(() => setGenMsgIdx((i) => (i + 1) % GEN_MESSAGES.length), 3500);
      return () => { clearInterval(pollRef.current); clearInterval(msgTicker); };
    }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, [data.campaign, load]);

  if (loading) return <div data-testid="campaign-detail-loading" className="flex items-center justify-center min-h-[60vh] text-[#64748B]">Loading…</div>;
  if (!data.campaign) return <div className="p-10 text-[#64748B]">Campaign not found.</div>;

  const c = data.campaign;
  const g = goalFor(c.goal_key);
  const Icon = g.icon;
  const progress = c.generation_progress || {};
  const isGenerating = c.status === "generating";
  const isFailed = c.status === "failed";

  const onDelete = async () => {
    if (!window.confirm(`Delete "${c.name}"? All linked pieces will be archived.`)) return;
    try { await api.delete(`/campaigns/${id}`); toast.success("Deleted"); nav("/campaigns"); }
    catch { toast.error("Could not delete"); }
  };

  const onRename = async () => {
    const next = window.prompt("New campaign name", c.name);
    if (!next || !next.trim() || next === c.name) return;
    try { await api.put(`/campaigns/${id}`, { name: next.trim() }); await load(); toast.success("Renamed"); }
    catch { toast.error("Could not rename"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-10 py-8 lg:py-12" data-testid="campaign-detail-page">
      <div className="flex items-center justify-between mb-6">
        <Link to="/campaigns" className="inline-flex items-center gap-1.5 text-[13px] text-[#64748B] hover:text-[#0A2342]"><ArrowLeft className="w-3.5 h-3.5" /> Campaigns</Link>
        <div className="flex items-center gap-1.5">
          <button onClick={onRename} data-testid="campaign-rename-btn" className="p-2 rounded-md text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0A2342]" title="Rename"><Pencil className="w-4 h-4" /></button>
          <button onClick={onDelete} data-testid="campaign-delete-btn" className="p-2 rounded-md text-[#64748B] hover:bg-[#FEF2F2] hover:text-[#B91C1C]" title="Delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Narrative Arc hero */}
      <div className="relative overflow-hidden rounded-3xl mb-10 shadow-xl">
        <div className="absolute inset-0 bg-cover bg-center opacity-20 mix-blend-overlay" style={{ backgroundImage: "url('https://images.pexels.com/photos/3721304/pexels-photo-3721304.jpeg?auto=compress&cs=tinysrgb&h=650&w=940')" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A2342] to-[#1D4E89]" style={{ mixBlendMode: "multiply" }} />
        <div className="relative z-10 p-8 md:p-14 text-white">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${g.color}33`, color: g.color }}><Icon className="w-5 h-5" /></div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[#4FD1C5] font-bold">{g.name}</div>
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-5xl leading-tight tracking-tight" data-testid="campaign-title">{c.name}</h1>
          <div className="flex items-center gap-4 mt-4 text-[12.5px] text-white/80">
            <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> {c.duration_days} days · starts {c.start_date}</span>
            <span className="opacity-40">·</span>
            <span>{c.credits_spent > 0 ? `${c.credits_spent} credits` : "Free (Pro Unlimited)"}</span>
          </div>

          {c.narrative_arc && (
            <div className="mt-8 pt-8 border-t border-white/15" data-testid="campaign-narrative-arc">
              <div className="text-[10.5px] tracking-[0.28em] uppercase font-bold text-[#4FD1C5] mb-3">Director&apos;s statement</div>
              <p className="font-heading font-medium text-xl md:text-3xl leading-tight tracking-tight">&ldquo;{c.narrative_arc.theme_statement}&rdquo;</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <div>
                  <div className="text-[10.5px] tracking-[0.28em] uppercase font-bold text-[#4FD1C5] mb-1">Tone</div>
                  <div className="text-white/90 text-[14px]">{c.narrative_arc.tone_modifier}</div>
                </div>
                <div>
                  <div className="text-[10.5px] tracking-[0.28em] uppercase font-bold text-[#4FD1C5] mb-1">Hero CTA</div>
                  <div className="text-white/90 text-[14px]">{c.narrative_arc.hero_cta}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {isGenerating && (
        <div data-testid="campaign-generating-state" className="dms-card p-8 mb-8 flex flex-col items-center text-center">
          <Loader2 className="w-8 h-8 text-[#4FD1C5] animate-spin mb-4" />
          <div className="font-heading font-bold text-xl text-[#0A2342]">Generating your campaign</div>
          <div className="text-[13.5px] text-[#64748B] mt-2 min-h-[20px]">{GEN_MESSAGES[genMsgIdx]}</div>
          {progress.total > 0 && (
            <div className="w-full max-w-md mt-6">
              <div className="h-1 w-full bg-[#F1F5F9] rounded-full overflow-hidden">
                <div className="h-full bg-[#4FD1C5] transition-all duration-500" style={{ width: `${Math.round((progress.completed / progress.total) * 100)}%` }} />
              </div>
              <div className="text-[11.5px] text-[#64748B] mt-2">{progress.completed} of {progress.total} pieces</div>
            </div>
          )}
        </div>
      )}

      {isFailed && (
        <div className="dms-card p-6 mb-8 border-[#EF4444]/30 bg-[#FEF2F2]">
          <div className="font-heading font-bold text-[#B91C1C]">Generation failed</div>
          <div className="text-[13px] text-[#64748B] mt-1">{c.generation_error || "Please retry or contact support."}</div>
        </div>
      )}

      {/* Timeline */}
      {c.plan && c.plan.length > 0 && (
        <div className="relative pt-4">
          <div className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#64748B] mb-6 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#4FD1C5]" /> Timeline
          </div>
          <div className="absolute left-[27px] md:left-[47px] top-16 bottom-2 w-px bg-[#E2E8F0]" />
          {c.plan.map((piece) => {
            const PIcon = PIECE_TYPE_ICON[piece.type] || PIECE_TYPE_ICON[piece.tool] || Sparkles;
            const label = PIECE_TYPE_LABEL[piece.type] || PIECE_TYPE_LABEL[piece.tool] || piece.type;
            return (
              <div key={piece.vault_id} data-testid={`campaign-timeline-day-${piece.day}`} className="grid grid-cols-[56px_1fr] md:grid-cols-[96px_1fr] gap-4 md:gap-8 relative py-4">
                <div className="flex flex-col items-center pt-1 relative">
                  <div className="absolute -top-1 text-[36px] md:text-[54px] font-heading font-black text-[#F1F5F9] leading-none z-0 select-none">{String(piece.day).padStart(2, "0")}</div>
                  <div className="w-11 h-11 md:w-14 md:h-14 rounded-full bg-white border-2 border-[#0A2342] flex items-center justify-center z-10 text-[#0A2342]"><PIcon className="w-4 h-4 md:w-5 md:h-5" /></div>
                </div>
                <div className="dms-card p-5 md:p-6 hover:border-[#4FD1C5]/50 transition-colors">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="dms-badge bg-[#0A2342]/5 text-[#0A2342] border-[#0A2342]/10">{label}</span>
                    <span className="text-[11.5px] text-[#64748B]">{piece.date}</span>
                  </div>
                  <div className="font-heading font-bold text-[15.5px] md:text-[17px] text-[#0A2342] leading-snug">{piece.title.split(":").slice(-1)[0].trim() || piece.title}</div>
                  <Link to={`/vault/${piece.vault_id}`} className="mt-3 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#0A2342] hover:text-[#4FD1C5]" data-testid={`campaign-piece-open-${piece.vault_id}`}>
                    Open in Vault <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
